import { Box, Typography, TextField, Slider, Select, MenuItem, Stack, IconButton, Tooltip, Divider } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import { useTranslation } from 'react-i18next';
import { MAP_LABEL_FONTS } from '@common/schemas/entities/location.schema';
import type { MapLabel } from '@common/schemas/entities/location.schema';

interface LabelsPanelProps {
    mapLabels: MapLabel[];
    selectedLabelUid: string | null;
    updateLabel: (uid: string, updates: Partial<Omit<MapLabel, 'uid'>>) => void;
    deleteLabel: (uid: string) => void;
    onCommit: () => void;
}

export function LabelsPanel({
    mapLabels,
    selectedLabelUid,
    updateLabel,
    deleteLabel,
    onCommit,
}: LabelsPanelProps) {
    const { t } = useTranslation();

    const selectedLabel = mapLabels.find(l => l.uid === selectedLabelUid);

    if (!selectedLabel) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary" align="center">
                    {t('labels.noSelection')}
                </Typography>
            </Box>
        );
    }

    const handleChange = (updates: Partial<Omit<MapLabel, 'uid'>>) => {
        updateLabel(selectedLabel.uid, updates);
    };

    return (
        <Stack spacing={3} sx={{ p: 2 }}>
            <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                    {t('labels.text')}
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    value={selectedLabel.text}
                    onChange={(e) => handleChange({ text: e.target.value })}
                    onBlur={onCommit}
                />
            </Box>

            <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                    {t('labels.fontFamily')}
                </Typography>
                <Select
                    fullWidth
                    size="small"
                    value={selectedLabel.fontFamily}
                    onChange={(e) => {
                        handleChange({ fontFamily: e.target.value as MapLabel['fontFamily'] });
                        onCommit();
                    }}
                >
                    {MAP_LABEL_FONTS.map(font => (
                        <MenuItem key={font} value={font} style={{ fontFamily: font }}>
                            {font}
                        </MenuItem>
                    ))}
                </Select>
            </Box>

            <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                    {t('labels.fontSize')}
                </Typography>
                <Slider
                    size="small"
                    value={selectedLabel.fontSize}
                    min={8}
                    max={120}
                    onChange={(_, val) => handleChange({ fontSize: val as number })}
                    onChangeCommitted={onCommit}
                />
            </Box>

            <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                    {t('labels.rotation')}
                </Typography>
                <Slider
                    size="small"
                    value={selectedLabel.rotation}
                    min={-Math.PI}
                    max={Math.PI}
                    step={0.01}
                    onChange={(_, val) => handleChange({ rotation: val as number })}
                    onChangeCommitted={onCommit}
                />
            </Box>

            <Stack direction="row" spacing={1} alignItems="center">
                <Box flex={1}>
                    <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                        {t('labels.color')}
                    </Typography>
                    <input
                        type="color"
                        value={selectedLabel.color}
                        onChange={(e) => handleChange({ color: e.target.value })}
                        onBlur={onCommit}
                        style={{ width: '100%', height: '32px', cursor: 'pointer', padding: 0, border: 'none' }}
                    />
                </Box>
                <Box flex={1}>
                    <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                        {t('labels.outlineColor')}
                    </Typography>
                    <input
                        type="color"
                        value={selectedLabel.outlineColor}
                        onChange={(e) => handleChange({ outlineColor: e.target.value })}
                        onBlur={onCommit}
                        style={{ width: '100%', height: '32px', cursor: 'pointer', padding: 0, border: 'none' }}
                    />
                </Box>
            </Stack>

            <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                    {t('labels.outlineWidth')}
                </Typography>
                <Slider
                    size="small"
                    value={selectedLabel.outlineWidth}
                    min={0}
                    max={10}
                    onChange={(_, val) => handleChange({ outlineWidth: val as number })}
                    onChangeCommitted={onCommit}
                />
            </Box>

            <Stack direction="row" spacing={1}>
                <Tooltip title={t('labels.bold')}>
                    <IconButton
                        color={selectedLabel.fontWeight === 'bold' ? 'primary' : 'default'}
                        onClick={() => {
                            handleChange({ fontWeight: selectedLabel.fontWeight === 'bold' ? 'normal' : 'bold' });
                            onCommit();
                        }}
                    >
                        <FormatBoldIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title={t('labels.italic')}>
                    <IconButton
                        color={selectedLabel.fontStyle === 'italic' ? 'primary' : 'default'}
                        onClick={() => {
                            handleChange({ fontStyle: selectedLabel.fontStyle === 'italic' ? 'normal' : 'italic' });
                            onCommit();
                        }}
                    >
                        <FormatItalicIcon />
                    </IconButton>
                </Tooltip>
            </Stack>

            <Divider />

            <Tooltip title={t('labels.delete')}>
                <IconButton
                    color="error"
                    onClick={() => {
                        deleteLabel(selectedLabel.uid);
                        onCommit();
                    }}
                >
                    <DeleteIcon />
                </IconButton>
            </Tooltip>
        </Stack>
    );
}
